import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { listFilesInStorageFolder } from "../../../../api/firebase/firestoreService";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import {
  BASE_PATH,
  currentMonth,
  currentYear,
  DOWNLOAD_FILES_TABLE_KEYS,
  FileItem,
} from "../constants";
import { triggerDownloadFromUrl } from "../utils/triggerDownloadFromUrl";

export interface UseDownloadFilesModalReturn {
  year: number;
  month: string;
  setYear: (year: number) => void;
  setMonth: (month: string) => void;
  files: FileItem[];
  loading: boolean;
  initialLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  handleFetch: () => Promise<void>;
  tableData: Record<string, unknown>[];
  pendingTableData: Record<string, unknown>[];
  nonPendingTableData: Record<string, unknown>[];
  handleDownloadIconClick: (
    downloadUrl: string,
    filename?: string,
  ) => Promise<void>;
}

export const useDownloadFilesModal = (): UseDownloadFilesModalReturn => {
  const orgId = getOrgIdForFetchExternalWorklist();

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const folderPath = `${BASE_PATH}/${orgId}/${year}/${month}`;

  const handleFetch = useCallback(async () => {
    if (!orgId) {
      setError("Organization not found");
      setInitialLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setFiles([]);
    setHasFetched(true);

    try {
      const result = await listFilesInStorageFolder(folderPath);
      result.sort((a, b) => b.name.localeCompare(a.name));
      setFiles(result);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load files";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [orgId, year, month, folderPath]);

  useEffect(() => {
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only once when modal opens
  }, []);

  const tableData = useMemo(
    () =>
      files.map((file, index) => ({
        [DOWNLOAD_FILES_TABLE_KEYS.FILE_NAME]: file.name,
        [DOWNLOAD_FILES_TABLE_KEYS.DOWNLOAD]: {
          id: `download-${index}-${file.fullPath}`,
          isEnabled: true,
          filePath: file.downloadURL,
          fileName: file.name,
        },
      })),
    [files],
  );

  const pendingTableData = useMemo(
    () =>
      files
        .filter((file) => file.name.toLowerCase().includes("pending"))
        .map((file, index) => ({
          [DOWNLOAD_FILES_TABLE_KEYS.FILE_NAME]: file.name,
          [DOWNLOAD_FILES_TABLE_KEYS.DOWNLOAD]: {
            id: `download-pending-${index}-${file.fullPath}`,
            isEnabled: true,
            filePath: file.downloadURL,
            fileName: file.name,
          },
        })),
    [files],
  );

  const nonPendingTableData = useMemo(
    () =>
      files
        .filter((file) => !file.name.toLowerCase().includes("pending"))
        .map((file, index) => ({
          [DOWNLOAD_FILES_TABLE_KEYS.FILE_NAME]: file.name,
          [DOWNLOAD_FILES_TABLE_KEYS.DOWNLOAD]: {
            id: `download-response-${index}-${file.fullPath}`,
            isEnabled: true,
            filePath: file.downloadURL,
            fileName: file.name,
          },
        })),
    [files],
  );

  const handleDownloadIconClick = useCallback(
    async (downloadUrl: string, filename?: string) => {
      const resolvedFilename = filename ?? "download";
      try {
        await triggerDownloadFromUrl(downloadUrl, resolvedFilename);
        toast.success("File downloaded successfully");
      } catch {
        toast.error("Error downloading file");
      }
    },
    [],
  );

  return {
    year,
    month,
    setYear,
    setMonth,
    files,
    loading,
    initialLoading,
    error,
    hasFetched,
    handleFetch,
    tableData,
    pendingTableData,
    nonPendingTableData,
    handleDownloadIconClick,
  };
};
